import React, { type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InfoListItem, ListItemTag } from '@brightlayer-ui/react-components';
import { Avatar, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronRight from '@mui/icons-material/ChevronRight';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import { useTypedSelector } from '@fiji/common/src/app/store';
import {
    useAcknowledgeEnvironmentalStressMonitoringMutation,
    useGetEnvironmentalStressMonitoringFilterQuery,
} from '@fiji/common/src/features/prediction/predictionApi';
import type { EnvironmentalFilterRecord } from '@fiji/common/src/features/prediction/environmentalStressMonitoringApi';
import { COMPONENTS, ELEMENTS, FEATURES, tid } from '@fiji/common/src/testing/testId';
import { formatEsmNotificationDate } from 'utils/environmentalStressMonitoring';

export const ESM_CRITICAL_TIME_WINDOW_SECONDS = 30 * 24 * 60 * 60;
const ESM_CRITICAL_PAGE_SIZE = 5;
const CRITICAL_COLOR = '#CA3C3D';

const dashboardTestId = tid[FEATURES.DASHBOARD_MANAGEMENT];
export const ESM_CRITICAL_HEADER_TEST_IDS = {
    card: dashboardTestId(COMPONENTS.BANNER, undefined, undefined, 'esm-critical'),
    cardRow: (id: string | number): string =>
        dashboardTestId(COMPONENTS.CARD, undefined, undefined, 'esm-critical', id),
    row: (id: string | number): string => dashboardTestId(COMPONENTS.BANNER, undefined, undefined, 'esm-critical', id),
    fullView: (id: string | number): string =>
        dashboardTestId(COMPONENTS.BANNER, ELEMENTS.BTN, undefined, 'esm-critical-full-view', id),
    acknowledge: (id: string | number): string =>
        dashboardTestId(COMPONENTS.BANNER, ELEMENTS.BTN, undefined, 'esm-critical-acknowledge', id),
} as const;

type EsmCriticalHeaderProps = {
    deviceId?: string;
    organizationId?: string;
};

const toDate = (timestamp: string | number): Date =>
    new Date(typeof timestamp === 'number' && Math.abs(timestamp) < 1e12 ? timestamp * 1000 : timestamp);

const isToday = (date: Date): boolean => date.toDateString() === new Date().toDateString();

export const getCriticalRecords = (records: EnvironmentalFilterRecord[] | undefined): EnvironmentalFilterRecord[] =>
    records?.filter((record) => record.severity?.toLowerCase() === 'alarm') ?? [];

export const EsmCriticalHeader = ({ deviceId, organizationId }: EsmCriticalHeaderProps): JSX.Element | null => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const realm = useTypedSelector((state) => state.common.selectedRealm);
    const [acknowledgedKeys, setAcknowledgedKeys] = React.useState<Set<string>>(() => new Set());
    const [acknowledgingKeys, setAcknowledgingKeys] = React.useState<Set<string>>(() => new Set());
    const [acknowledgeEsm] = useAcknowledgeEnvironmentalStressMonitoringMutation();

    const { currentData, refetch } = useGetEnvironmentalStressMonitoringFilterQuery(
        {
            deviceId: deviceId ?? '',
            organizationId,
            window: 'DAILY',
            timeWindow: ESM_CRITICAL_TIME_WINDOW_SECONDS,
            page: 0,
            size: ESM_CRITICAL_PAGE_SIZE,
            sort: { key: 'insightsdatetime', sortType: 'DESC' },
            severity: 'ALARM',
            useOpenDateTimeWindow: false,
        },
        { skip: !deviceId || !organizationId, refetchOnMountOrArgChange: true }
    );

    const records = getCriticalRecords(currentData?.data?.records).filter(
        (record) => !acknowledgedKeys.has(`${record.id ?? record.parameter}-${record.openDate}`)
    );

    if (!records.length) return null;

    return (
        <Stack data-testid={ESM_CRITICAL_HEADER_TEST_IDS.card} spacing={0.5}>
            {records.map((record) => {
                const recordKey = `${record.id ?? record.parameter}-${record.openDate}`;
                const occurredAt = toDate(record.insightsdatetime || record.openDate);
                const time = occurredAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                const day = isToday(occurredAt)
                    ? t('WIDGETS:ESM_TODAY', { defaultValue: 'Today' })
                    : formatEsmNotificationDate(occurredAt.getTime());
                const handleAcknowledge = async (): Promise<void> => {
                    if (!record.id) return;
                    setAcknowledgingKeys((previous) => new Set(previous).add(recordKey));
                    try {
                        await acknowledgeEsm({ esmId: record.id, organizationId }).unwrap();
                        setAcknowledgedKeys((previous) => new Set(previous).add(recordKey));
                        await refetch();
                    } catch {
                        // baseApi reports acknowledgement failures through the shared error toast.
                    } finally {
                        setAcknowledgingKeys((previous) => {
                            const next = new Set(previous);
                            next.delete(recordKey);
                            return next;
                        });
                    }
                };

                return (
                    <Box
                        key={recordKey}
                        data-testid={ESM_CRITICAL_HEADER_TEST_IDS.cardRow(record.id ?? recordKey)}
                        sx={{
                            border: '1px solid rgba(66, 78, 84, 0.12)',
                            borderBottom: 0,
                            borderRadius: '4px',
                            boxShadow: 1,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <InfoListItem
                            data-testid={ESM_CRITICAL_HEADER_TEST_IDS.row(record.id ?? recordKey)}
                            sx={{ height: 72, minHeight: 72, boxShadow: 'none' }}
                            title={
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Stack sx={{ flexShrink: 0 }}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            sx={{ color: CRITICAL_COLOR, fontSize: 12, lineHeight: '16px' }}
                                        >
                                            {t('WIDGETS:ESM_CRITICAL', { defaultValue: 'CRITICAL' }).toUpperCase()}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: 12, lineHeight: '16px' }}
                                        >{`${time} - ${day}`}</Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <ListItemTag
                                            label={t('WIDGETS:ESM_ACTIVE', { defaultValue: 'ACTIVE' })}
                                            backgroundColor={CRITICAL_COLOR}
                                        />
                                        <Typography variant="body1" sx={{ fontSize: 18, lineHeight: '24px' }}>
                                            {record.displayName}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            }
                            iconAlign="center"
                            statusColor={CRITICAL_COLOR}
                            icon={
                                <Avatar sx={{ bgcolor: CRITICAL_COLOR }}>
                                    <NotificationsActive sx={{ color: '#fff' }} />
                                </Avatar>
                            }
                            rightComponent={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        data-testid={ESM_CRITICAL_HEADER_TEST_IDS.acknowledge(record.id ?? recordKey)}
                                        disabled={acknowledgingKeys.has(recordKey) || !record.id}
                                        startIcon={
                                            acknowledgingKeys.has(recordKey) ? (
                                                <CircularProgress
                                                    size={14}
                                                    color="inherit"
                                                    data-testid={`${ESM_CRITICAL_HEADER_TEST_IDS.acknowledge(
                                                        record.id ?? recordKey
                                                    )}-loading`}
                                                />
                                            ) : undefined
                                        }
                                        sx={{
                                            width: acknowledgingKeys.has(recordKey) ? 144 : 117,
                                            minWidth: acknowledgingKeys.has(recordKey) ? 144 : 117,
                                            height: 35,
                                            padding: '8.5px 16px 8.5px 12px',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '8px',
                                            borderColor: 'rgba(66, 78, 84, 0.12)',
                                            borderRadius: '4px',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            lineHeight: '18px',
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap',
                                        }}
                                        onClick={(event): void => {
                                            event.stopPropagation();
                                            void handleAcknowledge();
                                        }}
                                    >
                                        {t('WIDGETS:ESM_ACKNOWLEDGE', { defaultValue: 'Acknowledge' })}
                                    </Button>
                                    <IconButton
                                        size="small"
                                        aria-label={t('WIDGETS:ESM_VIEW_ALL', {
                                            defaultValue: 'View All Environmental Stress Monitoring',
                                        })}
                                        data-testid={ESM_CRITICAL_HEADER_TEST_IDS.fullView(record.id ?? recordKey)}
                                        onClick={(event): void => {
                                            event.stopPropagation();
                                            navigate(
                                                `/${realm}/environmental-stress-monitoring${
                                                    deviceId ? `/${encodeURIComponent(deviceId)}` : ''
                                                }`
                                            );
                                        }}
                                    >
                                        <ChevronRight color="action" />
                                    </IconButton>
                                </Stack>
                            }
                        />
                    </Box>
                );
            })}
        </Stack>
    );
};
